import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

export default function AuthCallbackPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { loginWithToken } = useAuth()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const handleCallback = async () => {
      const token = searchParams.get('token')
      const errorParam = searchParams.get('error')

      if (errorParam) {
        setStatus('error')
        const errorMessages: Record<string, string> = {
          oauth_not_configured: 'Google Sign-In is not configured.',
          invalid_state: 'Invalid authentication state. Please try again.',
          token_exchange_failed: 'Failed to authenticate with Google.',
          failed_to_get_user_info: 'Failed to get user information from Google.',
          login_failed: 'Login failed. User may not be registered.',
          account_inactive: 'Your account is inactive.',
          no_tenant_access: 'You do not have access to any organization.',
          tenant_inactive: 'Your organization is inactive.',
        }
        setError(errorMessages[errorParam] || 'An error occurred during sign-in.')
        return
      }

      if (!token) {
        setStatus('error')
        setError('No authentication token received.')
        return
      }

      try {
        await loginWithToken(token)
        setStatus('success')
        // Redirect to dashboard after a brief delay
        setTimeout(() => {
          navigate('/', { replace: true })
        }, 1000)
      } catch (err) {
        setStatus('error')
        setError('Failed to complete authentication. Please try again.')
        console.error('Auth callback error:', err)
      }
    }

    handleCallback()
  }, [searchParams, loginWithToken, navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <CardTitle className="text-2xl font-bold">
            {status === 'loading' && 'Signing you in...'}
            {status === 'success' && 'Welcome!'}
            {status === 'error' && 'Sign-in Failed'}
          </CardTitle>
          <CardDescription>
            {status === 'loading' && 'Please wait while we complete your authentication.'}
            {status === 'success' && 'You have been successfully authenticated.'}
            {status === 'error' && 'There was a problem signing you in.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center space-y-4">
          {status === 'loading' && (
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
          )}
          
          {status === 'success' && (
            <CheckCircle2 className="h-12 w-12 text-green-500" />
          )}
          
          {status === 'error' && (
            <>
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <Button
                onClick={() => navigate('/login', { replace: true })}
                className="w-full"
              >
                Back to Login
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

